import { useTranslation } from 'react-i18next';
import type { MoleculeRegistration } from '@peruselab/api-client/molecules';
import { Icon } from '../../../components/Icon';
import { useActiveTenant } from '../../../tenancy/activeTenant';

/**
 * #1064 4c (#630, owner Q1/Q2): the reader's own space's record of a molecule -- who in it drew it
 * and when, whether it was first in PeruseLab, and an outside record found later (A1: evidence; it
 * takes nothing away). Nothing about any other space.
 */
export function MoleculeCredit({ registration }: { readonly registration: MoleculeRegistration }) {
  const { t, i18n } = useTranslation();
  const tenant = useActiveTenant();
  const contributions = registration.contributions ?? [];
  const external = registration.externalRecord ?? null;
  if (contributions.length === 0 && external === null) return null;

  const date = (iso: string) => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(iso));
  const first = registration.firstInPeruseLab ? ` · ${t('moleculeCredit.first')}` : '';
  const onlyYou = contributions.length === 1 && contributions[0]!.you;
  const people = new Intl.ListFormat(i18n.language, { type: 'conjunction' }).format(
    contributions.map((contribution) =>
      t('moleculeCredit.personDate', {
        name: contribution.you ? t('moleculeCredit.you') : (contribution.name ?? t('moleculeCredit.aMember')),
        date: date(contribution.at)
      })
    )
  );
  const space = tenant.active ? (tenant.nameOf(tenant.active.id) ?? tenant.active.name) : null;

  return (
    <p className="molecule-private-badge" data-testid="molecule-credit">
      <Icon name="edit" size="1rem" />
      {contributions.length > 0 ? (
        <span>
          {onlyYou
            ? t('moleculeCredit.youDrew', { date: date(contributions[0]!.at) })
            : space
              ? t('moleculeCredit.drawnIn', { space, people })
              : t('moleculeCredit.drawnBy', { people })}
          {first}
        </span>
      ) : null}
      {external ? (
        <span data-testid="molecule-external-record">
          {external.source === 'pubchem' && external.reference ? (
            <a
              href={`https://pubchem.ncbi.nlm.nih.gov/compound/${external.reference}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('moleculeCredit.pubchemRecord', { date: date(external.foundAt) })}
            </a>
          ) : (
            t('moleculeCredit.outsideRecord', { date: date(external.foundAt) })
          )}
        </span>
      ) : null}
    </p>
  );
}
